import React, { useRef } from 'react';
import { DataTable, ChartControls, LoadingSpinner } from '../ui';
import { formatNumber, formatPercentage } from '../../utils/formatters';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useRefineCustomerDissatisfiedProducts } from '../../hooks/useRefineCustomerQueries';

interface DissatisfiedProductsProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const DissatisfiedProducts: React.FC<DissatisfiedProductsProps> = ({
  branchId = 0,
  showLoading = true,
  onError
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'dissatisfied-products';
  const componentTitle = 'محصولات با بیشترین نارضایتی';
  
  // Fetch data using individual hook
  const { data: dissatisfiedProducts, isLoading, error } = useRefineCustomerDissatisfiedProducts(branchId);

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری…"
            />
    );
  }

  if (error || !dissatisfiedProducts) {
    return null;
  }

  // Table columns for dissatisfied products
  const dissatisfiedProductColumns = [
    {
      key: 'productName',
      title: 'نام محصول',
      dataIndex: 'productName',
    },
    {
      key: 'reasonCount',
      title: 'تعداد دلایل',
      dataIndex: 'reasonCount',
      sortable: true,
    },
    {
      key: 'reviewCount',
      title: 'تعداد نظر ناراضی',
      dataIndex: 'reviewCount',
    },
    {
      key: 'wareCount',
      title: 'تعداد فروش',
      dataIndex: 'wareCount',
      render: (value: number) => formatNumber(value),
    },
    {
      key: 'dissatisfactionShare',
      title: 'سهم از دلایل نارضایتی',
      dataIndex: 'dissatisfactionShare',
      render: (value: number) => formatPercentage(value * 100),
    },
    {
      key: 'percentRank',
      title: 'رتبه',
      dataIndex: 'percentRank',
      render: (value: number) => value,
    },
  ];

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="table"
        dashboardSource="refineCustomer"
        onInfo={() => openGuide(CHART_GUIDES.dissatisfiedProducts)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'refineCustomer',
          componentType: 'table',
          description: 'جدول محصولات با بیشترین نارضایتی مشتریان',
          props: { branchId, showLoading, onError },
          gridLayout: {
            containerClass: 'card-base card-padding'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card">
        {componentTitle}
      </h3>

      <DataTable
        columns={dissatisfiedProductColumns}
        data={dissatisfiedProducts || []}
      />
    </div>
  );
};

export default DissatisfiedProducts;